import {
  addDoc,
  getDocs,
  updateDoc,
  doc,
  collection,
  query,
  where,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';

export type DeletionRequestStatus = 'pending' | 'completed' | 'rejected';

export type SupportedLanguage = 'es' | 'en' | 'pt';

export interface DeletionRequest {
  id: string;
  email: string;
  reason?: string;
  language?: SupportedLanguage;
  status: DeletionRequestStatus;
  requestedAt: Timestamp;
  processedAt?: Timestamp | null;
  processedBy?: string | null;
  processedNote?: string | null;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_REASON_LENGTH = 500;
const SUPPORTED_LANGUAGES: SupportedLanguage[] = ['es', 'en', 'pt'];

export const createDeletionRequest = async (
  email: string,
  reason?: string,
  language?: string
): Promise<string> => {
  const normalizedEmail = (email || '').trim().toLowerCase();
  if (!normalizedEmail || !EMAIL_REGEX.test(normalizedEmail)) {
    throw new Error('deletion.error_invalid_email');
  }
  if (reason && reason.length > MAX_REASON_LENGTH) {
    throw new Error('deletion.error_reason_too_long');
  }

  const payload: Record<string, unknown> = {
    email: normalizedEmail,
    status: 'pending' as DeletionRequestStatus,
    requestedAt: serverTimestamp(),
  };
  if (reason) payload.reason = reason;
  if (language && SUPPORTED_LANGUAGES.includes(language as SupportedLanguage)) {
    payload.language = language;
  }

  const ref = await addDoc(collection(db, 'deletion_requests'), payload);
  return ref.id;
};

export const getPendingDeletionRequests = async (): Promise<DeletionRequest[]> => {
  const q = query(
    collection(db, 'deletion_requests'),
    where('status', '==', 'pending')
  );
  const snapshot = await getDocs(q);
  const requests: DeletionRequest[] = [];
  snapshot.forEach(d => requests.push({ id: d.id, ...(d.data() as Omit<DeletionRequest, 'id'>) }));
  requests.sort((a, b) => {
    const tA = a.requestedAt?.toMillis?.() ?? 0;
    const tB = b.requestedAt?.toMillis?.() ?? 0;
    return tA - tB;
  });
  return requests;
};

export const markDeletionAsCompleted = async (
  requestId: string,
  moderatorEmail: string,
  note?: string
): Promise<void> => {
  if (!requestId) throw new Error('deletion.error_invalid_request');
  if (!moderatorEmail) throw new Error('deletion.error_invalid_moderator');

  const payload: any = {
    status: 'completed' as DeletionRequestStatus,
    processedBy: moderatorEmail,
    processedAt: serverTimestamp(),
  };
  if (note) payload.processedNote = note;

  await updateDoc(doc(db, 'deletion_requests', requestId), payload);
};

export const rejectDeletionRequest = async (
  requestId: string,
  moderatorEmail: string,
  reason?: string
): Promise<void> => {
  if (!requestId) throw new Error('deletion.error_invalid_request');
  if (!moderatorEmail) throw new Error('deletion.error_invalid_moderator');

  const payload: any = {
    status: 'rejected' as DeletionRequestStatus,
    processedBy: moderatorEmail,
    processedAt: serverTimestamp(),
  };
  if (reason) payload.processedNote = reason;

  await updateDoc(doc(db, 'deletion_requests', requestId), payload);
};
